// ─── Cotisations réelles Jan/Fév/Mar 2026 — Relevés BFC OI ───────────────────
// Source : relevés bancaires SDASSP-FO Réunion
// Appliqué UNE SEULE FOIS via le garde _cotisations2026Seeded dans migrate()
// Ne remplace jamais une cotisation déjà saisie manuellement.

const P = 'paye';
const D = 'defaut_provision';

// Normalisation identique au parser (sans accents, majuscules, tirets → espaces)
const norm = s =>
  (s || '').toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[-']/g, ' ')
    .trim().replace(/\s+/g, ' ');

// Distance de Levenshtein (pour le matching flou prénoms)
function lev(a, b) {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (__, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i-1] === b[j-1]
        ? dp[i-1][j-1]
        : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
  return dp[m][n];
}

// Résoudre nom+prenom → adhérent dans la liste
// Stratégie : exact > dernier-mot-prenom-simple > Levenshtein ≤ 2
function findAdherent(adherents, nom, prenom) {
  const nNom = norm(nom);
  const nPre = norm(prenom);
  const words = nPre.split(' ');

  // Pass 1 : exact nom + exact prenom
  let hit = adherents.find(a => norm(a.nom) === nNom && norm(a.prenom) === nPre);
  if (hit) return hit;

  // Pass 2 : exact nom + prenom adhérent = UN seul mot inclus dans les mots du prenom seed
  hit = adherents.find(a => {
    if (norm(a.nom) !== nNom) return false;
    const ap = norm(a.prenom);
    if (ap.includes(' ')) return false;        // prenom adhérent composé → pas cette passe
    return words.includes(ap);                 // ex: "DANIELLE" ∈ ["MARIE","DANIELLE"] ✓
  });
  if (hit) return hit;

  // Pass 3 : exact nom + Levenshtein prenom ≤ 2
  let best = null, bestD = 3;
  for (const a of adherents) {
    if (norm(a.nom) !== nNom) continue;
    const d = lev(nPre, norm(a.prenom));
    if (d < bestD) { bestD = d; best = a; }
  }
  return best;
}

// ─── Données de cotisation Jan/Fév/Mar 2026 ───────────────────────────────────
// Format : [nom, prenom, { mois → statut | { status, motif } }]
// Pour les statuts simples (P), le motif est calculé automatiquement "Relevé BFC MM/2026"
const ENTRIES = [
  // ── Payés sur les 3 mois (Jan + Fév + Mar) ──────────────────────────────────
  ['ABRIEL',         'Frédéric',         {1:P, 2:P, 3:P}],
  ['ANCILE',         'Marie Corinne',    {1:P, 2:P, 3:P}],
  ['ANTAYA',         'Stéphanie',        {1:P, 2:P, 3:P}],
  ['ATHON',          'Larissa',          {1:P, 2:P, 3:P}],
  ['BAILLOEUL',      'Laëtitia',         {1:P, 2:P, 3:P}],
  ['BEAUMONT',       'Régis',            {1:P, 2:P, 3:P}],
  ['BENARD',         'Johan',            {1:P, 2:P, 3:P}],
  ['BIROTA',         'Bernard',          {1:P, 2:P, 3:P}],
  ['BOUZAT',         'Coralie',          {1:P, 2:P, 3:P}],
  ['BOYER',          'Fabrice',          {1:P, 2:P, 3:P}],
  ['BUCHLE',         'Vanessa',          {1:P, 2:P, 3:P}],
  ['CANTALIA',       'Audrey',           {1:P, 2:P, 3:P}],
  ['CENDIER',        'Henry Paul',       {1:P, 2:P, 3:P}],
  ['CLAIN',          'Marie',            {1:P, 2:P, 3:P}],
  ['DANEZ',          'Christine',        {1:P, 2:P, 3:P}],
  ['DANEZ',          'Jean François',    {1:P, 2:P, 3:P}],
  ['DEURWEILHER',    'Damien',           {1:P, 2:P, 3:P}],
  ['DIJOUX',         'Sophie',           {1:P, 2:P, 3:P}],
  ['ETHEVE',         'Berty',            {1:P, 2:P, 3:P}],
  ['FIRAGUAY',       'Dina',             {1:P, 2:P, 3:P}],
  ['FIRMANG',        'Vanessa',          {1:P, 2:P, 3:P}],
  ['FRAUMENS',       'Emmanuelle',       {1:P, 2:P, 3:P}],
  ['FRUTEAU',        'Marie Danielle',   {1:P, 2:P, 3:P}],
  ['GERVILLE',       'Marie',            {1:P, 2:P, 3:P}],
  ['GOULAND',        'Graziella',        {1:P, 2:P, 3:P}],
  ['GOULLY',         'Sabrina',          {1:P, 2:P, 3:P}],
  ['GOUMANE',        'Mirella',          {1:P, 2:P, 3:P}],
  ['GRONDIN',        'Marie',            {1:P, 2:P, 3:P}],
  ['GROSSET',        'Iris',             {1:P, 2:P, 3:P}],
  ['HOARAU',         'Marie Anne',       {1:P, 2:P, 3:P}],
  ['HOARAU',         'Yann',             {1:P, 2:P, 3:P}],
  ['HOAREAU',        'Jennyfer',         {1:P, 2:P, 3:P}],
  ['JALLET',         'Glen',             {1:P, 2:P, 3:P}],
  ['LAURET',         'Magaly',           {1:P, 2:P, 3:P}],
  ['LEBON',          'Sandrine',         {1:P, 2:P, 3:P}],
  ['LEBON',          'Stéphane',         {1:P, 2:P, 3:P}],
  ['LEBON',          'Valérie',          {1:P, 2:P, 3:P}],
  ['MALET',          'Cécile',           {1:P, 2:P, 3:P}],
  ['MARCOURT',       'Claudia',          {1:P, 2:P, 3:P}],
  ['MESRINE-ORTIZ',  'Élodie',           {1:P, 2:P, 3:P}],
  ['MOY-KIN',        'Yann',             {1:P, 2:P, 3:P}],
  ['NARCISSE',       'Annie',            {1:P, 2:P, 3:P}],
  ['NARIA',          'Véronique',        {1:P, 2:P, 3:P}],
  ['OMERALY',        'Elodie',           {1:P, 2:P, 3:P}],
  ['OULEDI',         'Expédit',          {1:P, 2:P, 3:P}],
  ['PAYET',          'Alexandre',        {1:P, 2:P, 3:P}],
  ['PAYET',          'Carole',           {1:P, 2:P, 3:P}],
  ['PAYET',          'Jérémie',          {1:P, 2:P, 3:P}],
  ['PAYET',          'Ruddy',            {1:P, 2:P, 3:P}],
  ['PERIBE',         'Fabrice',          {1:P, 2:P, 3:P}],
  ['PITOU',          'Olivier',          {1:P, 2:P, 3:P}],
  ['PLESSIER',       'Georgia',          {1:P, 2:P, 3:P}],
  ['SIMITAMBE',      'Anna',             {1:P, 2:P, 3:P}],
  ['SINNY PALANY',   'Judith',           {1:P, 2:P, 3:P}],
  ['SURVEILLE',      'Carinne',          {1:P, 2:P, 3:P}],
  ['THERMEA',        'Nadine',           {1:P, 2:P, 3:P}],
  ['XAVIER',         'Jean Michel',      {1:P, 2:P, 3:P}],

  // ── Fév + Mar seulement ─────────────────────────────────────────────────────
  ['CHARDONNERET',   'Samuela',          {2:P, 3:P}],

  // ── Jan + Mar (absent Fév) ───────────────────────────────────────────────────
  ['PAYET',          'Morgan',           {1:P, 3:P}],
  ['TELLIEZ',        'Julie',            {1:P, 3:P}],

  // ── Jan seulement ───────────────────────────────────────────────────────────
  ['GIRON',          'Arno',             {1:P}],
  ['MOREL',          'Samuel',           {1:P}],

  // ── Fév seulement ───────────────────────────────────────────────────────────
  ['LAGARRIGUE',     'Christine',        {2:P}],

  // ── Régularisation totale 2026 (Mar = virement 288€) ─────────────────────────
  ['MAILLOT',        'Louise',           {
    1:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    2:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    3:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    4:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    5:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    6:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    7:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    8:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    9:  { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    10: { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    11: { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
    12: { status: P, motif: 'Régularisation 2026 — virement 288€ (03/2026)' },
  }],

  // ── Impayés ──────────────────────────────────────────────────────────────────
  ['DAGARD',  'Marie Sandra', {
    1: { status: D, motif: 'Rejet BFC 01/2026 — Compte bloqué' },
    2: { status: D, motif: 'Rejet BFC 02/2026 — Provision insuffisante (2ème défaut)' },
  }],
  ['DARID',   'Geneviève', {
    1: { status: D, motif: 'Rejet BFC 01/2026 — Provision insuffisante' },
    2: { status: D, motif: 'Rejet BFC 02/2026 — Provision insuffisante (2ème défaut)' },
    3: { status: D, motif: 'Rejet BFC 03/2026 — Provision insuffisante (3ème défaut — action requise)' },
  }],
  ['HOARAU',  'Judith', {
    2: { status: D, motif: 'Rejet BFC 02/2026 — Provision insuffisante (prélèvement trimestriel 27€)' },
  }],
  ['SERY',    'Mickaël', {
    3: { status: D, motif: 'Rejet BFC 03/2026 — Service spécialisé banque du tiré' },
  }],
  ['TESTAN',  'Emeline', {
    3: { status: D, motif: 'Rejet BFC 03/2026 — Contestation débiteur 54€ — alerte rouge' },
  }],
];

// ─── Fonction d'application ────────────────────────────────────────────────────
export function applySeedCotisations2026(state) {
  if (state._cotisations2026Seeded) return state;

  const adherents = state.adherents || [];
  const cots = JSON.parse(JSON.stringify(state.cotisations || {}));
  const year = 2026;
  if (!cots[year]) cots[year] = {};

  let applied = 0;
  const notFound = [];

  for (const [nom, prenom, moisMap] of ENTRIES) {
    const adh = findAdherent(adherents, nom, prenom);
    if (!adh) {
      notFound.push(`${nom} ${prenom}`);
      continue;
    }
    if (!cots[year][adh.id]) cots[year][adh.id] = {};

    for (const [mStr, val] of Object.entries(moisMap)) {
      const month = parseInt(mStr, 10);
      if (cots[year][adh.id][month]) continue; // ne pas écraser

      const mm = String(month).padStart(2, '0');
      if (typeof val === 'string') {
        const motif = val === P ? `Relevé BFC ${mm}/${year}` : `Rejet BFC ${mm}/${year}`;
        cots[year][adh.id][month] = { status: val, motif };
      } else {
        cots[year][adh.id][month] = val;
      }
      applied++;
    }
  }

  if (notFound.length > 0) {
    console.info('[seedCotisations2026] Adhérents non trouvés (à pointer manuellement) :', notFound.join(', '));
  }
  console.info(`[seedCotisations2026] ${applied} cotisations 2026 chargées.`);

  return { ...state, cotisations: cots, _cotisations2026Seeded: true };
}
